/**
 * PREMIUM LANDING PAGE - FULL VERSION
 * 
 * All original conversion-optimized content with premium dark theme
 * Includes: Pricing with toggle, FAQs, ChatGPT comparison, ROI calculator, etc.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// React 19 native metadata
import { RoastCompareWidget } from '../roast/WebsiteRoast';
import { OracleWidget } from '../oracle/OraclePredictor';
import { ArtemisWidget } from '../artemis/ArtemisHunter';
import FreeTasteWarRoom from '../FreeTasteWarRoom';
import { FeedbackModal } from '../FeedbackModal';

// ============================================================
// BACKGROUND EFFECTS
// ============================================================

const FloatingOrbs = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Primary dramatic orbs */}
    <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-purple-600/25 rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '8s' }} />
    <div className="absolute top-1/3 -right-48 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[180px] animate-pulse" style={{ animationDuration: '10s' }} />
    <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-cyan-500/20 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '6s' }} />
    <div className="absolute top-2/3 right-1/3 w-[350px] h-[350px] bg-pink-500/15 rounded-full blur-[140px] animate-pulse" style={{ animationDuration: '12s' }} />
    
    {/* Additional accent orbs for depth */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-purple-500/10 via-transparent to-transparent rounded-full" />
    <div className="absolute top-20 left-1/3 w-32 h-32 bg-violet-400/20 rounded-full blur-[60px] animate-bounce" style={{ animationDuration: '15s' }} />
    <div className="absolute bottom-40 right-1/4 w-24 h-24 bg-cyan-400/25 rounded-full blur-[50px] animate-bounce" style={{ animationDuration: '12s' }} />
  </div>
);

// Particle field effect - pre-computed positions for purity
const PARTICLE_POSITIONS = [
  { left: '5%', top: '10%', delay: '0s', duration: '18s' },
  { left: '15%', top: '25%', delay: '2s', duration: '22s' },
  { left: '25%', top: '15%', delay: '1s', duration: '20s' },
  { left: '35%', top: '80%', delay: '3s', duration: '16s' },
  { left: '45%', top: '45%', delay: '0.5s', duration: '24s' },
  { left: '55%', top: '70%', delay: '4s', duration: '19s' },
  { left: '65%', top: '20%', delay: '1.5s', duration: '21s' },
  { left: '75%', top: '60%', delay: '2.5s', duration: '17s' },
  { left: '85%', top: '35%', delay: '3.5s', duration: '23s' },
  { left: '95%', top: '85%', delay: '0.8s', duration: '20s' },
  { left: '10%', top: '55%', delay: '2.2s', duration: '18s' },
  { left: '20%', top: '90%', delay: '4.5s', duration: '22s' },
  { left: '30%', top: '5%', delay: '1.2s', duration: '16s' },
  { left: '40%', top: '65%', delay: '3.8s', duration: '25s' },
  { left: '50%', top: '30%', delay: '0.3s', duration: '19s' },
  { left: '60%', top: '95%', delay: '2.8s', duration: '21s' },
  { left: '70%', top: '40%', delay: '1.8s', duration: '17s' },
  { left: '80%', top: '75%', delay: '4.2s', duration: '23s' },
  { left: '90%', top: '50%', delay: '0.6s', duration: '20s' },
  { left: '98%', top: '12%', delay: '3.2s', duration: '18s' },
];

const ParticleField = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {PARTICLE_POSITIONS.map((pos, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
        style={{
          left: pos.left,
          top: pos.top,
          animationDelay: pos.delay,
          animationDuration: pos.duration,
        }}
      />
    ))}
  </div>
);

// Hero spotlight effect
const HeroSpotlight = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Central radial spotlight */}
    <div 
      className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[800px]"
      style={{
        background: 'radial-gradient(ellipse at center top, rgba(139, 92, 246, 0.15) 0%, rgba(6, 182, 212, 0.08) 30%, transparent 70%)',
      }}
    />
    {/* Top edge glow */}
    <div 
      className="absolute top-0 left-0 right-0 h-px"
      style={{
        background: 'linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.5) 50%, transparent 100%)',
      }}
    />
  </div>
);

const GridPattern = () => (
  <div 
    className="fixed inset-0 pointer-events-none z-0 opacity-[0.02]"
    style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '80px 80px'
    }}
  />
);

// Greek Pillars - Framing the hero with mythological grandeur
const GreekPillars = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
    {/* Left Pillar */}
    <div className="absolute left-4 md:left-8 lg:left-16 top-20 bottom-0">
      <svg 
        className="h-[600px] w-16 md:w-20 lg:w-24 opacity-[0.12] hover:opacity-[0.18] transition-opacity duration-1000"
        viewBox="0 0 100 600" 
        fill="none"
        style={{ filter: 'drop-shadow(0 0 20px rgba(139, 92, 246, 0.3))' }}
      >
        {/* Capital (top decorative part) */}
        <path d="M10 40 L10 20 Q10 10 20 10 L80 10 Q90 10 90 20 L90 40" stroke="url(#pillarGradient)" strokeWidth="2" fill="none"/>
        <path d="M5 50 L95 50 L90 40 L10 40 Z" fill="url(#pillarGradient)" opacity="0.5"/>
        <rect x="15" y="50" width="70" height="8" fill="url(#pillarGradient)" opacity="0.4"/>
        
        {/* Volutes (scroll decorations) */}
        <ellipse cx="20" cy="30" rx="8" ry="6" stroke="url(#pillarGradient)" strokeWidth="1.5" fill="none"/>
        <ellipse cx="80" cy="30" rx="8" ry="6" stroke="url(#pillarGradient)" strokeWidth="1.5" fill="none"/>
        
        {/* Column shaft with fluting */}
        <rect x="20" y="58" width="60" height="480" fill="url(#pillarGradient)" opacity="0.15"/>
        {[0,1,2,3,4,5,6,7].map(i => (
          <line key={i} x1={25 + i*8} y1="58" x2={25 + i*8} y2="538" stroke="url(#pillarGradient)" strokeWidth="0.5" opacity="0.3"/>
        ))}
        
        {/* Entasis (subtle column curve) */}
        <path d="M20 58 Q15 300 20 538" stroke="url(#pillarGradient)" strokeWidth="1" fill="none" opacity="0.4"/>
        <path d="M80 58 Q85 300 80 538" stroke="url(#pillarGradient)" strokeWidth="1" fill="none" opacity="0.4"/>
        
        {/* Base */}
        <rect x="15" y="538" width="70" height="6" fill="url(#pillarGradient)" opacity="0.4"/>
        <rect x="10" y="544" width="80" height="8" fill="url(#pillarGradient)" opacity="0.3"/>
        <rect x="5" y="552" width="90" height="10" fill="url(#pillarGradient)" opacity="0.2"/>
        
        {/* Gradient definition */}
        <defs>
          <linearGradient id="pillarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="1"/>
            <stop offset="50%" stopColor="#818cf8" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.2"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
    
    {/* Right Pillar */}
    <div className="absolute right-4 md:right-8 lg:right-16 top-20 bottom-0">
      <svg 
        className="h-[600px] w-16 md:w-20 lg:w-24 opacity-[0.12] hover:opacity-[0.18] transition-opacity duration-1000"
        viewBox="0 0 100 600" 
        fill="none"
        style={{ filter: 'drop-shadow(0 0 20px rgba(6, 182, 212, 0.3))' }}
      >
        {/* Capital */}
        <path d="M10 40 L10 20 Q10 10 20 10 L80 10 Q90 10 90 20 L90 40" stroke="url(#pillarGradientRight)" strokeWidth="2" fill="none"/>
        <path d="M5 50 L95 50 L90 40 L10 40 Z" fill="url(#pillarGradientRight)" opacity="0.5"/>
        <rect x="15" y="50" width="70" height="8" fill="url(#pillarGradientRight)" opacity="0.4"/>
        
        {/* Volutes */}
        <ellipse cx="20" cy="30" rx="8" ry="6" stroke="url(#pillarGradientRight)" strokeWidth="1.5" fill="none"/>
        <ellipse cx="80" cy="30" rx="8" ry="6" stroke="url(#pillarGradientRight)" strokeWidth="1.5" fill="none"/>
        
        {/* Column shaft */}
        <rect x="20" y="58" width="60" height="480" fill="url(#pillarGradientRight)" opacity="0.15"/>
        {[0,1,2,3,4,5,6,7].map(i => (
          <line key={`r${i}`} x1={25 + i*8} y1="58" x2={25 + i*8} y2="538" stroke="url(#pillarGradientRight)" strokeWidth="0.5" opacity="0.3"/>
        ))}
        
        {/* Entasis */}
        <path d="M20 58 Q15 300 20 538" stroke="url(#pillarGradientRight)" strokeWidth="1" fill="none" opacity="0.4"/>
        <path d="M80 58 Q85 300 80 538" stroke="url(#pillarGradientRight)" strokeWidth="1" fill="none" opacity="0.4"/>
        
        {/* Base */}
        <rect x="15" y="538" width="70" height="6" fill="url(#pillarGradientRight)" opacity="0.4"/>
        <rect x="10" y="544" width="80" height="8" fill="url(#pillarGradientRight)" opacity="0.3"/>
        <rect x="5" y="552" width="90" height="10" fill="url(#pillarGradientRight)" opacity="0.2"/>
        
        {/* Gradient definition */}
        <defs>
          <linearGradient id="pillarGradientRight" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="1"/>
            <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#0891b2" stopOpacity="0.2"/>
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
);

// ============================================================
// UTILITY COMPONENTS
// ============================================================

const GradientText = ({ children, className = '', glow = false }) => (
  <span className={`
    bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 
    bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient
    ${glow ? 'drop-shadow-[0_0_35px_rgba(139,92,246,0.5)]' : ''}
    ${className}
  `}>
    {children}
  </span>
);

// Enhanced headline with glow effect
const GlowingHeadline = ({ children }) => (
  <span className="relative">
    <span className="relative z-10 text-white drop-shadow-[0_0_40px_rgba(255,255,255,0.3)]">
      {children}
    </span>
  </span>
);

const GlassCard = ({ children, className = '', hover = true }) => (
  <div className={`
    relative bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-gray-800
    ${hover ? 'hover:border-gray-700 hover:bg-gray-900/70 transition-all duration-300' : ''}
    ${className}
  `}>
    {children}
  </div>
);

const AnimatedCounter = ({ value, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const target = parseInt(value.toString().replace(/\D/g, '')) || 0;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value, isVisible]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// ============================================================
// SECTION COMPONENTS
// ============================================================

// Navigation
const Navigation = ({ onGetStarted, onLogin, isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-gray-900/80 backdrop-blur-2xl border-b border-gray-700/50 shadow-lg shadow-black/20' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI logo — AI marketing strategy platform" title="iSupplyAI — AI Marketing Strategy Platform" className="h-10 w-10 object-contain" />
            <span className="text-2xl font-bold"><GradientText>iSupplyAI</GradientText></span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-400 hover:text-white transition-colors font-medium">Features</a>
            <a href="#pricing" className="text-gray-400 hover:text-white transition-colors font-medium">Pricing</a>
            <a href="#faq" className="text-gray-400 hover:text-white transition-colors font-medium">FAQ</a>
            <Link to="/free-downloads" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium flex items-center gap-1">
              Resources
              <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-[10px] font-bold rounded">FREE</span>
            </Link>
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors font-medium">Blog</Link>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-400 hover:text-white p-2"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
            
            <button onClick={onLogin || onGetStarted} className="hidden sm:block text-gray-400 hover:text-white transition-colors font-medium">Login</button>
            <button onClick={onGetStarted} className="group relative px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 text-white overflow-hidden text-sm sm:text-base">
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700" />
              <span className="relative">Get Started</span>
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
            <div className="flex flex-col gap-4">
              <a href="#features" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors font-medium">Features</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors font-medium">Pricing</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors font-medium">FAQ</a>
              <Link to="/free-downloads" onClick={() => setMobileMenuOpen(false)} className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium flex items-center gap-2">
                Free Resources
                <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded">FREE PDFs</span>
              </Link>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors font-medium">Blog</Link>
              <button onClick={() => { (onLogin || onGetStarted)(); setMobileMenuOpen(false); }} className="text-gray-400 hover:text-white transition-colors font-medium text-left">Login</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onGetStarted, onDemoLogin, demoLoading, onShareFeedback }) => (
  <section className="relative pt-28 pb-16 px-6 overflow-hidden">
    {/* Hero-specific spotlight */}
    <HeroSpotlight />
    
    {/* Greek Pillars framing the hero */}
    <GreekPillars />
    
    <div className="relative max-w-7xl mx-auto text-center">
      {/* Top Feature Callout - Punchy value prop */}
      <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0s' }}>
        <p className="text-2xl md:text-3xl font-bold text-white">
          Content Creation <span className="text-cyan-400">On Steroids</span>
        </p>
        <p className="text-3xl sm:text-4xl md:text-5xl text-gray-300 mt-3">
          <span className="text-purple-400 font-bold">⚔️ AI Agents That Argue Your Strategy</span>
        </p>
        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-200 mt-4 max-w-3xl mx-auto leading-relaxed">
          Summon a council of AI strategists with distinct personalities. Watch them argue, cite real benchmarks, and forge battle plans for your business.
        </p>
      </div>
      
      {/* Badge - with animated gradient border */}
      <div className="inline-block mb-8 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 via-cyan-500 to-purple-600 rounded-full opacity-75 blur-sm group-hover:opacity-100 transition-opacity animate-gradient-border" />
          <div className="relative inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gray-900 backdrop-blur-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
            </span>
            <span className="text-sm font-medium text-gray-200">12 AI Gods • 700+ Benchmarks • Real-Time Strategy</span>
          </div>
        </div>
      </div>
      
      {/* Headline */}
      <h1 className="text-6xl md:text-8xl font-extrabold mb-6 leading-[1.1] tracking-tight">
        <span className="inline-block animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <GlowingHeadline>Summon AI Gods</GlowingHeadline>
        </span>
        <br />
        <span className="relative inline-block mt-2 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <GradientText glow className="drop-shadow-[0_0_60px_rgba(139,92,246,0.4)]">To Fight For Your Business</GradientText>
        </span>
      </h1>
      
      {/* Subheadline - The Divine Workflow explained simply */}
      <div className="max-w-3xl mx-auto mb-10 animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed mb-4">
          <span className="text-blue-400">🦉 Athena</span> scans competitors → 
          <span className="text-purple-400"> 📜 Hermes</span> creates content → 
          <span className="text-orange-400"> ☀️ Apollo</span> distributes everywhere
        </p>
        <p className="text-lg text-gray-500">
          One click. Full pipeline. <span className="text-purple-400 font-semibold">⚡ Divine Strike</span>
        </p>
      </div>
      
      {/* CTA - with shimmer effect */}
      <div className="flex flex-col sm:flex-row gap-5 justify-center mb-6 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
        <button onClick={onGetStarted} className="group relative px-10 py-5 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-2xl font-bold text-lg text-white hover:shadow-2xl hover:shadow-purple-500/40 hover:scale-105 transition-all duration-300 overflow-hidden">
          {/* Shimmer effect */}
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out animate-shimmer" />
          <span className="relative z-10 flex items-center justify-center gap-2">
            🏛️ Enter The Pantheon
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-2xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity -z-10" />
        </button>
        <button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="px-10 py-5 border-2 border-gray-700 rounded-2xl font-semibold text-lg text-gray-300 hover:bg-white/5 hover:border-gray-500 hover:text-white transition-all duration-300 backdrop-blur-sm">
          View Pricing
        </button>
      </div>
      
      {/* Try Live Demo CTA */}
      <div className="flex justify-center mb-12 animate-fadeInUp" style={{ animationDelay: '0.65s' }}>
        <button
          onClick={onDemoLogin}
          disabled={demoLoading}
          className="group relative flex items-center gap-2 px-6 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 hover:text-amber-200 font-semibold transition-all duration-300 hover:border-amber-400/50 disabled:opacity-60"
          data-testid="try-live-demo-btn"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
          </span>
          {demoLoading ? 'Loading demo...' : 'Try Free Demo — No Signup Required'}
          <span className="text-xs text-amber-400/60 ml-1">(10 min)</span>
        </button>
      </div>
      
      {/* 🔥 WEBSITE ROAST - The Viral Hook */}
      <div className="mb-12 animate-fadeInUp" style={{ animationDelay: '0.7s' }}>
        <div className="max-w-2xl mx-auto text-center mb-6">
          <p className="text-lg text-orange-400 font-bold flex items-center justify-center gap-2">
            <span>🔥</span> Try It Free - No Signup Required
          </p>
          <p className="text-gray-400 mt-1">7 AI gods analyze your site or learn from competitors</p>
        </div>
        <RoastCompareWidget />
      </div>
      
      {/* 🔮 THE ORACLE - Predictive Intelligence */}
      <div className="mb-12 animate-fadeInUp" style={{ animationDelay: '0.8s' }}>
        <div className="max-w-2xl mx-auto text-center mb-6">
          <p className="text-lg text-purple-400 font-bold flex items-center justify-center gap-2">
            <span>🔮</span> NEW: The Oracle
          </p>
          <p className="text-gray-400 mt-1">Know if your content will succeed before you publish</p>
        </div>
        <OracleWidget />
      </div>
      
      {/* 🏹 ARTEMIS - Lead Hunter */}
      <div className="mb-12 animate-fadeInUp" style={{ animationDelay: '0.85s' }}>
        <div className="max-w-2xl mx-auto text-center mb-6">
          <p className="text-lg text-emerald-400 font-bold flex items-center justify-center gap-2">
            <span>🏹</span> NEW: Artemis
          </p>
          <p className="text-gray-400 mt-1">Find people actively looking for your solution</p>
        </div>
        <ArtemisWidget />
      </div>
      
      {/* Scroll indicator */}
      <div className="flex flex-col items-center gap-2 animate-fadeInUp" style={{ animationDelay: '0.9s' }}>
        <span className="text-xs text-gray-500 uppercase tracking-widest">Explore</span>
        <div className="animate-bounce">
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </div>
  </section>
);

// Social Proof Ticker
const SocialProofTicker = () => (
  <section className="py-8 border-y border-gray-800/50 bg-gray-900/30 overflow-hidden">
    <div className="flex animate-scroll gap-12 items-center whitespace-nowrap">
      {[
        '🎉 "Generated 50 blog posts in 1 day!"',
        '💰 "Saved $5000/month on content writers"',
        '🚀 "10x faster content creation"',
        '⭐ "Best AI tool I\'ve ever used"',
        '💡 "Doubled our content output overnight"',
        '🎯 "Finally, AI that actually delivers"',
        '🎉 "Generated 50 blog posts in 1 day!"',
        '💰 "Saved $5000/month on content writers"',
      ].map((text, i) => (
        <span key={i} className="text-gray-500 text-lg px-4">{text}</span>
      ))}
    </div>
  </section>
);

// Featured In / Press Mentions — External backlinks for social proof
const FeaturedInSection = () => (
  <section className="py-10 px-6 border-b border-gray-800/30">
    <div className="max-w-5xl mx-auto">
      <p className="text-center text-xs uppercase tracking-[0.25em] text-gray-500 mb-6 font-medium">Featured &amp; Mentioned In</p>
      <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
        <a href="https://doc.clickup.com/90182059546/d/h/2kzma1gu-2098/a88d38f67c452f8" target="_blank" rel="noopener noreferrer"
           className="group flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-800/50 bg-gray-900/40 hover:border-purple-500/30 hover:bg-purple-500/5 transition-all duration-300"
           data-testid="featured-clickup">
          <svg className="w-5 h-5 text-purple-400 group-hover:text-purple-300 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 17l6-6 4 4 6-6" /><path d="M4 17h16" /></svg>
          <span className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors font-medium">ClickUp</span>
        </a>
        <a href="https://www.cake.me/me/isupply-ai" target="_blank" rel="noopener noreferrer"
           className="group flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-800/50 bg-gray-900/40 hover:border-blue-500/30 hover:bg-blue-500/5 transition-all duration-300"
           data-testid="featured-cake">
          <svg className="w-5 h-5 text-blue-400 group-hover:text-blue-300 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
          <span className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors font-medium">Cake.me</span>
        </a>
        <a href="https://the-article-network.blogspot.com/2026/01/ai-driven-supply-chain-optimization.html" target="_blank" rel="noopener noreferrer"
           className="group flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-800/50 bg-gray-900/40 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-300"
           data-testid="featured-article-network">
          <svg className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
          <span className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors font-medium">The Article Network</span>
        </a>
      </div>
    </div>
  </section>
);

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { 
      step: '01', 
      icon: '🎯', 
      title: 'Tell Us Your Goal', 
      description: 'Enter a topic, select content type, and choose your tone. Our AI understands your brand instantly.',
      gradient: 'from-purple-500 to-indigo-500'
    },
    { 
      step: '02', 
      icon: '⚡', 
      title: 'AI Creates Magic', 
      description: 'Our GPT-4o powered engine generates SEO-optimized, engaging content in under 30 seconds.',
      gradient: 'from-cyan-500 to-blue-500'
    },
    { 
      step: '03', 
      icon: '🚀', 
      title: 'Publish & Dominate', 
      description: 'Export to any platform, multiply across channels, and watch your traffic grow.',
      gradient: 'from-emerald-500 to-green-500'
    },
  ];

  return (
    <section className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
            <span className="text-cyan-400">⚡</span>
            <span className="text-cyan-400 text-sm font-bold">GET STARTED IN 3 SIMPLE STEPS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">From Zero to Content Hero in </span>
            <GradientText>Under 60 Seconds</GradientText>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">No learning curve. No complex setup. Just results.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connection lines */}
          <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-purple-500 via-cyan-500 to-emerald-500 opacity-30" />
          
          {steps.map((step, i) => (
            <div key={i} className="relative group">
              {/* Step number badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center font-bold text-lg text-white shadow-lg shadow-${step.gradient.split(' ')[0].replace('from-', '')}/30`}>
                  {step.step}
                </div>
              </div>
              
              <GlassCard className="pt-12 pb-8 px-6 text-center h-full hover:scale-[1.02] transition-all duration-300">
                {/* Icon */}
                <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center text-4xl mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  {step.icon}
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
              </GlassCard>
            </div>
          ))}
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-500 mb-4">🕐 Average time from signup to first content: <span className="text-cyan-400 font-bold">47 seconds</span></p>
        </div>
      </div>
    </section>
  );
};

// Trust Badges Section  
const TrustBadgesSection = () => (
  <section className="py-12 px-6 border-y border-gray-800/30">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
        {[
          { icon: '🔐', text: 'Enterprise-Grade Security', subtext: 'SOC 2 Type II' },
          { icon: '🌍', text: 'GDPR Compliant', subtext: 'EU Data Protected' },
          { icon: '⚡', text: '99.9% Uptime SLA', subtext: 'Enterprise Ready' },
          { icon: '💳', text: 'Secure Payments', subtext: 'Stripe Protected' },
          { icon: '💰', text: '30-Day Guarantee', subtext: 'Risk-Free Trial' },
        ].map((badge, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-2 bg-gray-900/50 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors">
            <span className="text-2xl">{badge.icon}</span>
            <div>
              <div className="text-sm font-semibold text-white">{badge.text}</div>
              <div className="text-xs text-gray-500">{badge.subtext}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => {
  const features = [
    { icon: '🍷', title: 'Dionysus Reddit Engine', description: 'Find real Reddit threads, craft authentic replies, build your brand reputation.', gradient: 'from-purple-500 to-pink-500' },
    { icon: '⚡', title: 'Divine Strike Pipeline', description: 'One click: Athena analyzes → Hermes creates → Apollo distributes. Magic.', gradient: 'from-amber-500 to-red-500' },
    { icon: '🦉', title: 'Athena Intelligence', description: 'Scans competitors, finds content gaps, delivers winning strategies.', gradient: 'from-amber-500 to-orange-500' },
    { icon: '📜', title: 'Hermes Content Forge', description: 'Blogs, social posts, images, emails - created in seconds, not hours.', gradient: 'from-cyan-500 to-emerald-500' },
    { icon: '☀️', title: 'Apollo Distribution', description: 'Humanized content across 5 platforms. Authentic, never robotic.', gradient: 'from-yellow-500 to-orange-500' },
    { icon: '🧠', title: 'The Hivemind', description: '700+ industry benchmarks from HubSpot, Unbounce & more. Compare your metrics against the market in real-time.', gradient: 'from-indigo-500 to-purple-500' },
    { icon: '🎮', title: 'Reddit Phase System', description: 'Seedling to Authority - grow your Reddit presence strategically.', gradient: 'from-green-500 to-teal-500' },
    { icon: '🎯', title: 'SEO Optimizer', description: 'Real-time SEO scoring with keyword analysis and ranking predictions.', gradient: 'from-pink-500 to-rose-500' },
    { icon: '🔮', title: 'Performance Predictions', description: 'Know how content will perform before you publish.', gradient: 'from-violet-500 to-purple-500' },
    { icon: '🏹', title: 'Artemis Lead Hunter', description: 'AI scans Reddit, Twitter, forums & the web for high-intent buyers already looking for your solution.', gradient: 'from-emerald-500 to-green-500' },
    { icon: '🪞', title: 'Mirror of Truth', description: 'Brutally honest brand assessment. See how you really stack up against 700+ industry standards.', gradient: 'from-sky-500 to-blue-500' },
    { icon: '✨', title: 'Divine Ascension', description: 'Your brand avatar evolves, unlocking new abilities as you grow.', gradient: 'from-blue-500 to-cyan-500' },
  ];

  return (
    <section id="features" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">Everything You Need to </span>
            <GradientText>Dominate Content</GradientText>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">One platform. Unlimited possibilities. Your AI workforce handles it all.</p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <GlassCard key={i} className="p-8 group">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
              <p className="text-gray-400">{feature.description}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 32 });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <span className="text-gray-400 text-sm">Offer expires in:</span>
      <div className="flex items-center gap-1">
        {[
          { value: timeLeft.hours, label: 'h' },
          { value: timeLeft.minutes, label: 'm' },
          { value: timeLeft.seconds, label: 's' },
        ].map((unit, i) => (
          <div key={i} className="flex items-center">
            <span className="bg-red-500/20 text-red-400 font-mono font-bold px-2 py-1 rounded text-lg">
              {String(unit.value).padStart(2, '0')}
            </span>
            <span className="text-red-400 text-sm ml-0.5 mr-1">{unit.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pricing Section - FULL FEATURE LISTS WITH CATEGORIES
const PricingSection = ({ onGetStarted, founderSpots }) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [expandedTier, setExpandedTier] = useState(null);

  // STARTER TIER - Basic features
  const starterFeatures = [
    { category: 'Credits & Usage', items: [
      { text: '50 credits per month', included: true },
      { text: '5 content types', included: true, desc: '(blog, social, email, marketing, product)' },
    ]},
    { category: 'Content Creation', items: [
      { text: 'Basic AI content generation', included: true },
      { text: '4 tone options', included: true },
      { text: 'Basic templates', included: true },
    ]},
    { category: 'Free Viral Tools', items: [
      { text: '🏹 Artemis Reddit Scan', included: true, desc: '(3 leads/hunt)' },
      { text: '🔥 Website Roast (limited)', included: true, desc: '7 AI gods roast any URL' },
      { text: '⚔️ Beat My Competitor', included: true, desc: 'AI competitive battle report' },
      { text: '📊 Strategy Score', included: true, desc: 'AI website analysis across 6 dimensions' },
      { text: '🔮 Oracle Preview', included: true },
    ]},
    { category: 'Not Included', items: [
      { text: 'Living War Room (12 AI gods debating)', included: false },
      { text: 'God Memory + Debate Verdicts', included: false },
      { text: 'Artemis full scanning (forums, web)', included: false },
      { text: 'AI God Agents (Hermes, Athena, Apollo)', included: false },
      { text: 'Save + Share debates', included: false },
    ]},
  ];

  // PRO TIER - Organized by category with ALL features
  const proFeatures = [
    { category: '🎯 Credits & Core', items: [
      { text: '500 credits per month', highlight: true },
      { text: 'All 5 content types + all tones', highlight: false },
      { text: 'Priority processing speed', highlight: false },
    ]},
    { category: '⚔️ The Living War Room', items: [
      { text: 'The Living War Room', highlight: true, desc: '12 AI gods debate your strategy live' },
      { text: '🗡️ Issue The Challenge', highlight: true, desc: 'Bring your own debate topic to the council' },
      { text: '🏛️ Seek Their Counsel', highlight: true, desc: 'Let the gods identify what needs debating' },
      { text: 'God Memory System', highlight: true, desc: 'Gods remember past debates + reference them' },
      { text: 'Debate Verdicts + Action Items', highlight: true, desc: 'Concrete conclusions, tracked follow-through' },
      { text: '"Side With" Dynamics', highlight: true, desc: 'Take sides, shift the debate live' },
      { text: 'Save + Share Debates', highlight: true, desc: 'Public shareable links (viral loop)' },
      { text: '12 Unique God Brains', highlight: true, desc: 'Each pulls real data before speaking' },
      { text: 'Free Taste War Room', highlight: false, desc: 'No-signup public demo' },
      { text: 'Privacy controls', highlight: false, desc: 'Your data, your rules' },
    ]},
    { category: '🦉 Athena Intelligence Room', items: [
      { text: 'Athena\'s War Room', highlight: true, desc: 'Full competitive intelligence HQ' },
      { text: 'Competitor Website Scraping', highlight: true, desc: 'Deep analysis of any competitor URL' },
      { text: 'Content Gap Analysis', highlight: true, desc: 'Find what competitors cover that you don\'t' },
      { text: 'Market Positioning Intel', highlight: true, desc: 'Pricing, messaging & positioning analysis' },
      { text: 'SWOT Analysis Generation', highlight: false, desc: 'AI-powered strengths/weaknesses/opportunities' },
      { text: 'Competitive Landscape Reports', highlight: false, desc: 'Exportable intelligence briefs' },
    ]},
    { category: '👟 Hermes Content Forge', items: [
      { text: 'Hermes\' Content Forge', highlight: true, desc: 'Full AI content creation studio' },
      { text: '📝 Blog Post Generation', highlight: true, desc: 'Long-form SEO-optimized articles' },
      { text: '📱 Social Media Content', highlight: true, desc: 'Platform-specific posts for all networks' },
      { text: '✉️ Email Campaigns', highlight: true, desc: 'Newsletters, drip sequences, cold outreach' },
      { text: '📢 Marketing Copy', highlight: true, desc: 'Ad copy, landing pages, product descriptions' },
      { text: '🌍 Multi-Language Support', highlight: false, desc: '10 languages' },
      { text: '🎭 Tone Control', highlight: false, desc: 'Professional, casual, bold, witty & more' },
      { text: '🎨 Brand Voice Analyzer', highlight: false, desc: 'Learn and match your unique style' },
    ]},
    { category: '☀️ Apollo Distribution Chamber', items: [
      { text: 'Apollo\'s Sun Chamber', highlight: true, desc: 'Multi-platform content distribution' },
      { text: '💼 LinkedIn Distribution', highlight: true },
      { text: '🐦 Twitter/X Distribution', highlight: true },
      { text: '📘 Facebook Distribution', highlight: true },
      { text: '📸 Instagram Distribution', highlight: true },
      { text: '📝 Medium Distribution', highlight: true },
      { text: 'Humanized Content', highlight: false, desc: 'AI rewrites for each platform\'s tone' },
    ]},
    { category: '⚡ Divine Strike Pipeline', items: [
      { text: 'One-click content pipeline', highlight: true, desc: 'Athena → Hermes → Apollo automation' },
      { text: 'Alliance War Room', highlight: true, desc: 'Command all agents from one control center' },
      { text: 'Scheduled pipeline runs', highlight: false },
    ]},
    { category: '🔥 Viral Features (No Signup)', items: [
      { text: '🔥 Website Roast', highlight: true, desc: '7 AI gods roast any URL' },
      { text: '⚔️ Beat My Competitor', highlight: true, desc: 'AI-powered competitive battle report' },
      { text: '📊 Strategy Score', highlight: true, desc: 'Shareable 6-dimension website analysis' },
      { text: '🎓 Competitor Learning Mode', highlight: true, desc: 'Learn from competitor sites' },
      { text: 'Shareable roast & score links', highlight: false },
    ]},
    { category: '🏹 Artemis Lead Hunter', items: [
      { text: '🔴 Reddit lead scanning', highlight: true, desc: 'Find people seeking your solution' },
      { text: '💬 Forums & Communities', highlight: true, desc: 'Quora, HN, IndieHackers, ProductHunt' },
      { text: '🌐 Web scanning', highlight: true, desc: 'Blogs, reviews, comparison articles' },
      { text: '💡 AI Outreach Tips', highlight: true, desc: 'Strategic approach advice per lead' },
      { text: '🔔 Lead Nurturing', highlight: true, desc: '7-day follow-up reminders' },
      { text: 'AI-crafted responses', highlight: false },
      { text: 'Intent & relevance scoring', highlight: false },
    ]},
    { category: '🍷 Dionysus Reddit Engine', items: [
      { text: 'Real Reddit thread scanning', highlight: true },
      { text: 'AI-crafted authentic replies', highlight: true },
      { text: '🎮 Account Phase System', highlight: true, desc: 'Seedling → Authority growth' },
      { text: 'Subreddit discovery', highlight: false },
    ]},
    { category: '📊 Analytics & Optimization', items: [
      { text: '🎯 SEO Optimizer', highlight: true, bold: true, desc: 'Real-time scoring + keywords' },
      { text: '🔮 Performance Predictor', highlight: true, bold: true, desc: 'Virality & conversion scores' },
      { text: '🔥 Trending Topics', highlight: true, bold: true, desc: 'Live data from NewsAPI' },
      { text: '📈 Viral Analyzer', highlight: true, bold: true, desc: 'Analyze what makes content go viral' },
      { text: 'The Oracle', highlight: true, desc: 'Predict performance before publishing' },
      { text: 'Mirror of Truth', highlight: true, desc: 'Benchmark against 700+ standards' },
      { text: 'The Hivemind', highlight: true, desc: '700+ industry benchmarks — CAC, conversion, SEO, social, ads & more' },
      { text: '💰 ROI Calculator', highlight: false, desc: 'Predict revenue before publishing' },
      { text: '😊 Sentiment Predictor', highlight: false, desc: 'Prevent PR disasters' },
      { text: '⚠️ Content Health Monitor', highlight: false, desc: 'Decay alerts & refresh tips' },
    ]},
    { category: '🔍 Website Analyzer', items: [
      { text: '🔍 Website Analyzer', highlight: true, desc: 'Brand voice + content strategy from any URL' },
      { text: '📊 Overview Analysis', highlight: false, desc: 'Full brand positioning breakdown' },
      { text: '🎭 Brand Voice Detection', highlight: false, desc: 'Tone, style, messaging analysis' },
      { text: '📈 SEO Analysis', highlight: false, desc: 'Keywords, structure, opportunities' },
      { text: '📝 Content Strategy', highlight: false, desc: 'Recommendations based on your site' },
      { text: '📢 Advertising Insights', highlight: false, desc: 'Ad copy & channel recommendations' },
    ]},
    { category: '🎨 Content Multiplication', items: [
      { text: '1 → 10+ platforms', highlight: true },
      { text: '1 → 17+ content formats', highlight: true },
      { text: '🔄 Content Repurposing Engine', highlight: false },
    ]},
    { category: '🟠 Quora Goldmine', items: [
      { text: 'Automated Quora answers', highlight: true },
      { text: 'Chrome Extension included', highlight: true },
      { text: 'Question discovery', highlight: false },
    ]},
    { category: '🛠️ Tools & Export', items: [
      { text: '📊 Content Brief Generator', highlight: false },
      { text: '📅 Content Calendar', highlight: false },
      { text: '💎 Smart Hashtag Generator', highlight: false },
      { text: '📤 Content Export', highlight: false, desc: 'PDF, DOCX, HTML' },
      { text: '📚 Premium templates library', highlight: false },
      { text: '📢 Ads Dashboard', highlight: false, desc: 'AI-generated ad copy + management' },
      { text: '📊 Analytics Dashboard', highlight: false, desc: 'Content performance tracking' },
      { text: '📝 Blog Admin Dashboard', highlight: true, desc: 'Manage posts from all content sources' },
      { text: '🔗 Auto-publishing webhooks', highlight: true, desc: 'Outrank, Opinly, BacklinkManagement' },
    ]},
    { category: '🎁 Extras', items: [
      { text: '🎁 Referral Program', highlight: false, desc: 'Earn credits' },
      { text: '⚡ Priority support', highlight: false },
      { text: '✨ Divine Ascension', highlight: false, desc: 'Brand avatar evolution' },
      { text: '📚 Free Resources Library', highlight: false, desc: 'Downloadable templates + guides' },
      { text: '🔐 Onboarding Wizard', highlight: false, desc: 'Guided setup + privacy controls' },
    ]},
  ];

  // ENTERPRISE TIER
  const enterpriseFeatures = [
    { category: '🚀 Credits & Power', items: [
      { text: '2,000 credits per month', highlight: true, desc: '4x more than Pro!' },
      { text: 'Everything in Pro included', highlight: true },
      { text: 'Fastest processing priority', highlight: true },
    ]},
    { category: '⚔️ Unlimited Living War Room', items: [
      { text: 'Unlimited Living War Room debates', highlight: true, desc: 'All 12 gods, unlimited sessions' },
      { text: '🗡️ Issue The Challenge (unlimited)', highlight: true },
      { text: '🏛️ Seek Their Counsel (unlimited)', highlight: true },
      { text: 'God Memory + Persistent Rivalries', highlight: true, desc: 'Gods evolve their positions over time' },
      { text: 'Unlimited Verdicts + Action Tracking', highlight: true },
      { text: 'Custom god configurations', highlight: false },
    ]},
    { category: '🦉 Unlimited Athena Intelligence', items: [
      { text: 'Unlimited competitor analyses', highlight: true },
      { text: 'Deep competitor scraping', highlight: true, desc: 'Full website + social + content audit' },
      { text: 'Real-time competitive alerts', highlight: false },
    ]},
    { category: '👟 Unlimited Hermes Forge', items: [
      { text: 'Unlimited content generation', highlight: true },
      { text: '🌍 25+ languages support', highlight: true },
      { text: 'Custom brand voice profiles', highlight: false },
    ]},
    { category: '☀️ Unlimited Apollo Distribution', items: [
      { text: 'Unlimited platform distributions', highlight: true },
      { text: 'Priority publishing queue', highlight: true },
    ]},
    { category: '📊 Full Analytics Suite', items: [
      { text: '🎯 SEO Optimizer (unlimited)', highlight: true, bold: true },
      { text: '🔮 Performance Predictor (unlimited)', highlight: true, bold: true },
      { text: '🔥 Trending Topics (unlimited)', highlight: true, bold: true },
      { text: '📈 Viral Analyzer (unlimited)', highlight: true, bold: true },
      { text: 'Unlimited Oracle predictions', highlight: true },
      { text: 'Full Hivemind Access', highlight: true, desc: 'All 13 benchmark categories + custom comparisons' },
      { text: '📊 Executive Dashboard', highlight: true },
      { text: '📢 Ad Performance Tracking', highlight: false, desc: 'Google + Facebook Ads' },
      { text: 'ROI attribution', highlight: false },
      { text: 'Custom reports', highlight: false },
    ]},
    { category: '🏹 Artemis Deep Hunt', items: [
      { text: '🔍 Deep Scan', highlight: true, desc: 'G2, Capterra, Trustpilot, news, job boards' },
      { text: 'Review site monitoring', highlight: true, desc: 'G2, Capterra, Trustpilot' },
      { text: 'Job posting signals', highlight: true, desc: 'Find growing companies' },
      { text: 'Competitor complaint threads', highlight: false },
      { text: 'Advanced lead nurturing', highlight: false },
    ]},
    { category: '🍷 Dionysus Unlimited', items: [
      { text: 'Unlimited Reddit scans', highlight: true },
      { text: 'Unlimited AI replies', highlight: true },
      { text: 'Multi-account management', highlight: false },
    ]},
    { category: '🤖 Automation & Scale', items: [
      { text: '🤖 Auto-Pilot Content Engine', highlight: true, desc: '90-day automated strategy!' },
      { text: '⚡ Bulk Content Generation', highlight: true, desc: '10 pieces at once!' },
      { text: '⚡ Divine Strike Priority', highlight: true, desc: 'Faster pipeline processing' },
      { text: 'Scheduled automation runs', highlight: false },
    ]},
    { category: '👥 Team & Collaboration', items: [
      { text: '👥 Unlimited team members', highlight: true },
      { text: 'Role-based permissions', highlight: false },
      { text: 'Team analytics dashboard', highlight: false },
      { text: 'Shared content library', highlight: false },
    ]},
    { category: '🔌 API & Integrations', items: [
      { text: '🔐 Full API Access', highlight: true, desc: 'Integrate anywhere' },
      { text: 'Webhook support', highlight: false },
      { text: '⚙️ Custom Integrations', highlight: false },
      { text: '📝 Blog Admin Dashboard', highlight: true, desc: 'Full content management + analytics' },
      { text: '🔗 Auto-publishing from 3 content services', highlight: true },
      { text: 'Zapier & Make compatible', highlight: false },
    ]},
    { category: '🏢 Enterprise Features', items: [
      { text: '🔒 99.9% SLA Guarantee', highlight: false },
    ]},
  ];

  // Count total features
  const countFeatures = (features) => features.reduce((acc, cat) => acc + cat.items.length, 0);

  return (
    <>
    {/* See It In Action + Free Taste */}
    <section id="demo" className="py-20 px-6 relative">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span className="text-purple-400 text-xs font-semibold uppercase tracking-widest">See It In Action</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-3">The Living War Room</h2>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto">
            12 AI gods with unique brains, debating your strategy in real-time. They disagree. They remember. They evolve. Watch it happen live.
          </p>
        </div>

        {/* Video Embed Slot — replace VIDEO_URL with your Clueso link */}
        <div className="mb-10" id="demo-video">
          <div className="rounded-2xl border border-gray-800 bg-gray-900/50 overflow-hidden aspect-video flex items-center justify-center" data-testid="demo-video-container">
            {/* Uncomment and replace with your Clueso video URL:
            <iframe 
              src="YOUR_CLUESO_VIDEO_URL" 
              title="iSupplyAI Demo"
              className="w-full h-full"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
            */}
            <div className="text-center px-6 py-12">
              <div className="flex items-center justify-center gap-1 mb-4">
                {['\u{1F3AD}','\u2694\uFE0F','\u{1F4B0}','\u{1F3C6}'].map((e, i) => (
                  <span key={i} className="text-2xl animate-bounce" style={{ animationDelay: `${i*150}ms` }}>{e}</span>
                ))}
              </div>
              <p className="text-gray-500 text-sm">Demo video coming soon</p>
              <p className="text-gray-600 text-xs mt-1">Try the live debate below instead</p>
            </div>
          </div>
        </div>

        {/* Free Taste War Room — live demo */}
        <div data-testid="free-taste-section">
          <FreeTasteWarRoom />
        </div>
      </div>
    </section>

    <section id="pricing" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6 animate-pulse">
            <span className="text-amber-400 animate-pulse">🔥</span>
            <span className="text-amber-400 text-sm font-bold">FOUNDERS PRICING: $50/mo (reg. $79) — Only {founderSpots ?? '...'} Spots Left!</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Choose Your Content Superpower</h2>
          <p className="text-xl text-gray-400">Lock in founder pricing before spots run out</p>
          
          <CountdownTimer />
          
          {/* Toggle */}
          <div className="mt-8 flex justify-center">
            <div className="bg-gray-800/50 backdrop-blur rounded-full p-1.5 border border-gray-700 inline-flex">
              <button onClick={() => setIsAnnual(false)} className={`px-6 py-2.5 rounded-full font-semibold transition-all ${!isAnnual ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white' : 'text-gray-400 hover:text-white'}`}>
                Monthly
              </button>
              <button onClick={() => setIsAnnual(true)} className={`px-6 py-2.5 rounded-full font-semibold transition-all relative ${isAnnual ? 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white' : 'text-gray-400 hover:text-white'}`}>
                Annual
                {!isAnnual && <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">-27%</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {/* Starter */}
          <GlassCard className="p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Starter</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-white">$0</span>
                <span className="text-gray-500">/month</span>
              </div>
              <p className="text-gray-400">Perfect for trying out AI content</p>
            </div>
            
            <div className="space-y-4 mb-8">
              {starterFeatures.map((category, ci) => (
                <div key={ci}>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{category.category}</div>
                  <ul className="space-y-2">
                    {category.items.map((f, i) => (
                      <li key={i} className={`flex items-start text-sm ${f.included ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span className={`mr-2 mt-0.5 ${f.included ? 'text-emerald-400' : 'text-gray-600'}`}>{f.included ? '✓' : '✗'}</span>
                        <span>{f.text}{f.desc && <span className="text-gray-500 ml-1">{f.desc}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            <button onClick={() => onGetStarted('free')} className="w-full py-3 rounded-xl font-bold bg-gray-800 text-white hover:bg-gray-700 transition-colors">
              Start Free Today
            </button>
            <p className="text-center text-gray-500 text-sm mt-3">🔓 No credit card required</p>
          </GlassCard>

          {/* Pro - Featured */}
          <div className="relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full text-sm font-bold text-white z-10">
              🔥 MOST POPULAR
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl blur-xl opacity-20" />
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl border-2 border-orange-500/50 p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
                <div className="mb-2">
                  {!isAnnual ? (
                    <>
                      <div className="text-xl text-gray-500 line-through mb-1">$79</div>
                      <span className="text-5xl font-bold text-white">$50</span>
                      <span className="text-gray-400">/mo</span>
                    </>
                  ) : (
                    <>
                      <div className="text-xl text-gray-500 line-through mb-1">$948/year</div>
                      <span className="text-5xl font-bold text-white">$690</span>
                      <span className="text-gray-400">/year</span>
                      <div className="mt-2 bg-emerald-500/20 text-emerald-400 inline-block px-3 py-1 rounded-full text-sm font-bold">
                        Save $258 (27% OFF)
                      </div>
                    </>
                  )}
                </div>
                <div className="inline-block bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm font-bold mt-2">
                  FOUNDERS PRICING — Only {founderSpots ?? '...'} spots left
                </div>
                <p className="text-amber-300/70 text-sm mt-2">$50/mo locked forever (goes to $79)</p>
                <p className="text-gray-500 text-xs mt-1">{countFeatures(proFeatures)}+ features included</p>
              </div>
              
              {/* Scrollable Feature List */}
              <div className="space-y-4 mb-6 max-h-[450px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-orange-500/30 scrollbar-track-transparent">
                {proFeatures.map((category, ci) => (
                  <div key={ci} className="border-b border-gray-700/50 pb-3 last:border-0">
                    <div className="text-xs font-bold text-orange-400 uppercase tracking-wider mb-2">{category.category}</div>
                    <ul className="space-y-1.5">
                      {category.items.map((f, i) => (
                        <li key={i} className={`flex items-start text-sm ${f.highlight ? 'text-amber-200' : 'text-gray-400'}`}>
                          <span className="text-amber-400 mr-2 mt-0.5 flex-shrink-0">✓</span>
                          <span>
                            <span className={f.bold ? 'font-extrabold text-white text-[15px]' : ''}>{f.text}</span>
                            {f.desc && <span className="text-amber-300/60 ml-1 text-xs">{f.desc}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              
              <button onClick={() => onGetStarted(isAnnual ? 'pro_annual' : 'pro')} className="w-full py-4 rounded-xl font-bold bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-lg hover:shadow-orange-500/30 transition-all hover:scale-[1.02]">
                Lock In $50/mo — Only {founderSpots ?? '...'} Spots Left
              </button>
              <p className="text-center text-amber-400/70 text-sm mt-3 font-medium">Founders rate locked forever. Regular price: $79/mo</p>
            </div>
          </div>

          {/* Enterprise */}
          <div className="relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-sm font-bold text-white z-10">
              🏆 UNLIMITED POWER
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-15" />
            <div className="relative bg-gradient-to-b from-blue-900/50 to-purple-900/30 rounded-2xl border-2 border-blue-500/40 p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
                <div className="mb-2">
                  {!isAnnual ? (
                    <>
                      <span className="text-5xl font-bold text-white">$249</span>
                      <span className="text-gray-400">/mo</span>
                    </>
                  ) : (
                    <>
                      <div className="text-xl text-gray-500 line-through mb-1">$2,988/year</div>
                      <span className="text-5xl font-bold text-white">$2,190</span>
                      <span className="text-gray-400">/year</span>
                      <div className="mt-2 bg-emerald-500/20 text-emerald-400 inline-block px-3 py-1 rounded-full text-sm font-bold">
                        Save $798 (27% OFF)
                      </div>
                    </>
                  )}
                </div>
                <p className="text-blue-300 mt-2">For agencies & serious creators</p>
                <p className="text-gray-500 text-xs mt-1">{countFeatures(enterpriseFeatures)}+ features included</p>
              </div>
              
              {/* Scrollable Feature List */}
              <div className="space-y-4 mb-6 max-h-[450px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-500/30 scrollbar-track-transparent">
                {enterpriseFeatures.map((category, ci) => (
                  <div key={ci} className="border-b border-blue-700/30 pb-3 last:border-0">
                    <div className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">{category.category}</div>
                    <ul className="space-y-1.5">
                      {category.items.map((f, i) => (
                        <li key={i} className={`flex items-start text-sm ${f.highlight ? 'text-blue-200' : 'text-gray-400'}`}>
                          <span className="text-blue-400 mr-2 mt-0.5 flex-shrink-0">✓</span>
                          <span>
                            <span className={f.bold ? 'font-extrabold text-white text-[15px]' : ''}>{f.text}</span>
                            {f.desc && <span className="text-blue-300/60 ml-1 text-xs">{f.desc}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              
              <button onClick={() => onGetStarted(isAnnual ? 'enterprise_annual' : 'enterprise')} className="w-full py-4 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:scale-[1.02]">
                Unlock Enterprise Power
              </button>
              <p className="text-center text-blue-300/70 text-sm mt-3">⚡ Instant access • Cancel anytime</p>
            </div>
          </div>
        </div>

        {/* Feature Comparison Note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            All plans include: 🔒 Secure payments via Stripe • 🔄 Cancel anytime • 📧 Email support • 🔐 Data encryption
          </p>
        </div>
      </div>
    </section>
    </>
  );
};

// Featured Debate — Real War Room session showcase
const FEATURED_DEBATE = {
  topic: "Should we prioritize personalized AI-driven email marketing campaigns to boost customer engagement, or focus on broad-based social media advertising to increase brand awareness this quarter?",
  gods: [
    { id: 'apollo', name: 'Apollo', emoji: '\u2600\uFE0F', title: 'God of Distribution', color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10' },
    { id: 'themis', name: 'Themis', emoji: '\u2696\uFE0F', title: 'The Data Purist', color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/10' },
    { id: 'eris', name: 'Eris', emoji: '\u2694\uFE0F', title: 'The Contrarian', color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/10' },
    { id: 'calliope', name: 'Calliope', emoji: '\u{1F3AD}', title: 'Creative Wildcard', color: 'text-pink-400', border: 'border-pink-500/30', bg: 'bg-pink-500/10' },
  ],
  highlights: [
    {
      god: 'apollo', mood: 'contemplative',
      text: "It is crucial we consider the timing and impact of both strategies. Personalized AI-driven email campaigns can forge deeper connections, leveraging the focused engagement of your audience. However, broad-based social media advertising can cast a wider net to spark initial interest. Perhaps a synthesis of both paths is warranted.",
    },
    {
      god: 'themis', mood: 'analytical',
      text: "The data suggests that prioritizing personalized AI-driven email marketing campaigns could yield better customer engagement, as your metrics show an impressive open rate of 42% and a CTR of 2.4%. Those numbers indicate a higher likelihood of generating meaningful interactions compared to broad-based social media advertising.",
    },
    {
      god: 'eris', mood: 'skeptical',
      text: "I'm not convinced that a broad-based social media approach is the answer. X/Twitter is down 48% YoY, and Instagram's down 28% — these are vital warning signs. Personalized AI-driven campaigns are performing better with an impressive 42% open rate. What could go WRONG if we split resources too thin?",
    },
    {
      god: 'calliope', mood: 'inspired',
      text: "Why not completely flip the script? Instead of picking sides, let's get weird with a hybrid approach! Picture this: you unleash personalized AI-driven campaigns that tap into your audience's desires, and then follow up with a social media blitz that creates buzz. Let's break the rules and forge uncharted pathways!",
    },
  ],
  userResponse: "I believe delegating 66% of our time to email campaigns and 33% to social media outreach is the right split. Email campaigns can be set up using highly targeted leads for free, automated with outreach tools — we stand to gain substantially while losing very little time.",
  outcome: "The debate produced a concrete 66/33 email-to-social strategy, defined the ideal customer profile, and generated email outreach templates — all in a single session."
};

const FeaturedDebateSection = ({ onGetStarted }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % FEATURED_DEBATE.highlights.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const activeMsg = FEATURED_DEBATE.highlights[activeIdx];
  const activeGod = FEATURED_DEBATE.gods.find(g => g.id === activeMsg.god);

  return (
    <section className="py-24 px-6 relative overflow-hidden" data-testid="featured-debate-section">
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/8 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-semibold">REAL DEBATE &mdash; NOT A DEMO</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Watch AI Gods Debate <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Real Strategy</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            This is an actual War Room session. A founder asked 4 AI gods whether to prioritize email marketing or social media. Here's what happened.
          </p>
        </div>

        {/* Debate topic */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="rounded-2xl border border-gray-700/50 bg-gray-900/50 backdrop-blur-sm p-6" data-testid="featured-debate-topic">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-sm">?</span>
              </div>
              <div>
                <p className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-2">The Challenge</p>
                <p className="text-white text-base leading-relaxed">{FEATURED_DEBATE.topic}</p>
              </div>
            </div>
          </div>
        </div>

        {/* God council pills */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          {FEATURED_DEBATE.gods.map((god, i) => (
            <button
              key={god.id}
              onClick={() => { setActiveIdx(i); setIsAutoPlaying(false); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 cursor-pointer ${
                activeIdx === i
                  ? `${god.bg} ${god.border} ${god.color} shadow-lg`
                  : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:border-gray-600'
              }`}
              data-testid={`featured-god-${god.id}`}
            >
              <span className="text-lg">{god.emoji}</span>
              <span className="text-sm font-medium">{god.name}</span>
              {activeIdx === i && <span className="text-xs opacity-60">{god.title}</span>}
            </button>
          ))}
        </div>

        {/* Active god's response */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className={`rounded-2xl border ${activeGod.border} ${activeGod.bg} p-6 transition-all duration-500`} data-testid="featured-debate-response">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-xl ${activeGod.bg} border ${activeGod.border} flex items-center justify-center`}>
                  <span className="text-2xl">{activeGod.emoji}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`font-bold ${activeGod.color}`}>{activeGod.name}</span>
                  <span className="text-gray-500 text-sm">{activeGod.title}</span>
                  <span className="text-gray-600 text-xs italic">({activeMsg.mood})</span>
                </div>
                <p className="text-gray-300 leading-relaxed">{activeMsg.text}</p>
              </div>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-4">
            {FEATURED_DEBATE.highlights.map((_, i) => (
              <button
                key={i}
                onClick={() => { setActiveIdx(i); setIsAutoPlaying(false); }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === activeIdx ? `w-8 ${activeGod.color.replace('text-', 'bg-')}` : 'bg-gray-700 hover:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* User response + outcome */}
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4 mb-10">
          <div className="rounded-2xl border border-purple-500/20 bg-purple-500/5 p-5">
            <p className="text-xs text-purple-400 font-semibold uppercase tracking-wider mb-2">The Founder Responded</p>
            <p className="text-gray-300 text-sm leading-relaxed">{FEATURED_DEBATE.userResponse}</p>
          </div>
          <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-5">
            <p className="text-xs text-green-400 font-semibold uppercase tracking-wider mb-2">Session Outcome</p>
            <p className="text-gray-300 text-sm leading-relaxed">{FEATURED_DEBATE.outcome}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={onGetStarted}
            className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-2xl font-bold text-white hover:shadow-2xl hover:shadow-purple-500/30 hover:scale-105 transition-all duration-300"
            data-testid="featured-debate-cta"
          >
            Start Your Own War Room Debate
          </button>
          <p className="text-gray-500 text-sm mt-3">Free to try &mdash; no credit card required</p>
        </div>
      </div>
    </section>
  );
};

// Strategy Score CTA Section
const StrategyScoreSection = () => {
  const [url, setUrl] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      navigate(`/strategy-score?url=${encodeURIComponent(url.trim())}`);
    } else {
      navigate('/strategy-score');
    }
  };

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent pointer-events-none" />
      <div className="relative max-w-5xl mx-auto">
        <div className="bg-gradient-to-br from-purple-900/20 via-[#0d0d15] to-cyan-900/20 border border-purple-500/20 rounded-3xl p-8 md:p-14 text-center">
          <p className="text-sm font-semibold tracking-widest text-cyan-400 uppercase mb-4">Free Tool — No Signup Required</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            What's Your <GradientText>Strategy Score</GradientText>?
          </h2>
          <p className="text-base text-gray-400 max-w-xl mx-auto mb-10">
            Enter any URL. Our AI analyzes your website across 6 strategy dimensions and delivers a shareable score card in seconds.
          </p>

          <form onSubmit={handleSubmit} className="max-w-xl mx-auto flex flex-col sm:flex-row gap-3 mb-8" data-testid="landing-score-form">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter your website URL..."
              className="flex-1 px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-base focus:outline-none focus:border-purple-500/50 transition-colors"
              data-testid="landing-score-input"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all text-base whitespace-nowrap"
              data-testid="landing-score-btn"
            >
              Get Free Score
            </button>
          </form>

          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-gray-500">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" />Positioning</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400" />Messaging</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-400" />SEO & Content</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400" />Trust & Proof</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-rose-400" />Conversion</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-400" />Competitive Edge</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing FAQ
const PricingFAQ = () => {
  const faqs = [
    { q: "What happens to unused credits?", a: "Credits reset each billing cycle to your plan's full allocation. We recommend using them throughout the month for maximum value!" },
    { q: "Can I get more credits?", a: "Upgrade your plan anytime to unlock more monthly credits. Pro gives you 500/month, Enterprise gives you 2,000/month." },
    { q: "What's your refund policy?", a: "14-day money-back guarantee. If you're not satisfied, we'll refund your payment—no questions asked." },
    { q: "Can I cancel anytime?", a: "Absolutely! Cancel anytime with one click. No contracts, no commitments." },
    { q: "Do I need a credit card for free tier?", a: "Nope! Start with our free Starter plan instantly—no credit card required." },
    { q: "What payment methods do you accept?", a: "All major credit cards (Visa, Mastercard, Amex) through secure Stripe payments." },
  ];

  return (
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-white mb-12">💬 Pricing FAQ</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {faqs.map((faq, i) => (
            <GlassCard key={i} className="p-6">
              <h3 className="font-bold text-white mb-2">❓ {faq.q}</h3>
              <p className="text-gray-400 text-sm">{faq.a}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// ChatGPT Comparison
const ChatGPTComparison = () => {
  const features = [
    { icon: '⚡', title: 'Content Multiplication', desc: '1 piece → 10+ platform-optimized versions', chatgpt: 'Manual copy/paste ❌', us: 'Instant + Best times ✅' },
    { icon: '🎯', title: 'Real-Time SEO Scoring', desc: 'SEO score, keyword density, ranking prediction', chatgpt: 'No SEO analysis ❌', us: 'Replaces $99-299/mo tools ✅' },
    { icon: '🔮', title: 'Performance Prediction', desc: 'Know if content will succeed BEFORE publishing', chatgpt: 'No prediction ❌', us: 'Avoid failed content ✅' },
    { icon: '🔥', title: 'Viral Content Analyzer', desc: 'Reverse engineer viral patterns', chatgpt: 'No viral analysis ❌', us: '8 pattern checks ✅' },
    { icon: '📈', title: 'Trending Topics Feed', desc: 'Real-time industry trends with search volume', chatgpt: 'Knowledge cutoff ❌', us: 'Live market data ✅' },
    { icon: '🎨', title: 'Brand Voice Learning', desc: 'Learns your voice, maintains consistency', chatgpt: 'Manual prompts ❌', us: 'Auto voice profile ✅' },
    { icon: '🎯', title: 'Content Gap Analysis', desc: 'Strategic planning vs competitors', chatgpt: "Can't analyze library ❌", us: 'Strategic recommendations ✅' },
    { icon: '💎', title: 'Smart Hashtags', desc: 'Data-driven with reach estimation', chatgpt: 'Generic, no data ❌', us: 'Engagement data ✅' },
    { icon: '🔄', title: 'Content Repurposing', desc: 'Blog → Podcast → Video → Email automated', chatgpt: 'Manual for each ❌', us: '6 formats templated ✅' },
    { icon: '📊', title: 'A/B Variations', desc: '3-5 variations for testing automatically', chatgpt: 'Request each manually ❌', us: 'One-click A/B ready ✅' },
    { icon: '🧠', title: 'The Hivemind', desc: '700+ industry benchmarks across 13 categories', chatgpt: 'No benchmark data ❌', us: 'Real-time industry comparisons ✅' },
    { icon: '⚔️', title: 'Living War Room', desc: '12 AI agents debate your strategy live', chatgpt: 'Single perspective ❌', us: 'Multi-agent debate with verdicts ✅' },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">🚀 10 Features ChatGPT Can&apos;t Do</h2>
          <p className="text-xl text-gray-400">Why settle for basic AI when you can have intelligence?</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {features.map((f, i) => (
            <GlassCard key={i} className="p-6 group">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-xl flex-shrink-0 group-hover:scale-110 transition-transform">
                  {f.icon}
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">{f.title}</h3>
                  <p className="text-gray-400 text-sm mb-2">{f.desc}</p>
                  <p className="text-red-400/80 text-xs">ChatGPT: {f.chatgpt}</p>
                  <p className="text-emerald-400 text-xs">iSupplyAI: {f.us}</p>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        <div className="text-center mt-12">
          <GlassCard className="inline-block p-8">
            <p className="text-xl text-gray-400 mb-2">ChatGPT = Content Generation</p>
            <p className="text-2xl font-bold"><GradientText>iSupplyAI = Content Intelligence Platform</GradientText></p>
            <p className="text-gray-500 mt-2">Same price as ChatGPT Plus but with 10 features it will never have</p>
          </GlassCard>
        </div>
      </div>
    </section>
  );
};

// Testimonials
const TestimonialsSection = () => {
  const testimonials = [
    { 
      name: "Sarah Johnson", 
      role: "Marketing Director @ TechFlow", 
      text: "Increased our content output by 500% while cutting costs by 80%. We went from 4 blog posts/month to 20. The AI agents are absolute game-changers.",
      avatar: "👩‍💼",
      metric: "500% more content",
      company: "TechFlow"
    },
    { 
      name: "Michael Chen", 
      role: "Founder & CEO @ GrowthLabs", 
      text: "From 2 hours per blog post to 5 minutes. We saved $47,000 in the first year on content writer costs. Paid for itself in the first week.",
      avatar: "👨‍💻",
      metric: "$47K saved/year",
      company: "GrowthLabs"
    },
    { 
      name: "Emma Davis", 
      role: "Content Creator & Influencer", 
      text: "Generated $50K in client revenue using iSupplyAI in my first 3 months. Now I manage 15 clients solo. Best investment I've made in my career.",
      avatar: "👩‍🎨",
      metric: "$50K revenue",
      company: "Self-employed"
    },
    { 
      name: "David Park", 
      role: "Head of Content @ ScaleUp", 
      text: "The SEO optimization is incredible. Our organic traffic grew 340% in 6 months. Athena found content gaps we never knew existed.",
      avatar: "👨‍💼",
      metric: "340% traffic growth",
      company: "ScaleUp"
    },
  ];

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
            <span className="text-amber-400">⭐⭐⭐⭐⭐</span>
            <span className="text-amber-400 text-sm font-bold">4.9/5 FROM 2,000+ REVIEWS</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Real Results from Real Creators</h2>
          <p className="text-xl text-gray-400">Join 10,000+ businesses transforming their content game</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <GlassCard key={i} className="p-6 group hover:border-purple-500/30">
              {/* Metric badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
                <span className="text-emerald-400 font-bold text-sm">{t.metric}</span>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-2xl">
                  {t.avatar}
                </div>
                <div>
                  <h4 className="font-bold text-white">{t.name}</h4>
                  <p className="text-sm text-gray-500">{t.role}</p>
                </div>
                <div className="ml-auto text-amber-400 text-sm">★★★★★</div>
              </div>
            </GlassCard>
          ))}
        </div>
        
        {/* Logo bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <p className="text-center text-gray-500 text-sm mb-6">Trusted by teams at</p>
          <div className="flex justify-center items-center gap-8 flex-wrap opacity-60">
            {['TechFlow', 'GrowthLabs', 'ScaleUp', 'ContentPro', 'MarketMax', 'BrandBoost'].map((company, i) => (
              <span key={i} className="text-gray-400 font-semibold text-lg">{company}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// General FAQ
const GeneralFAQ = () => {
  const faqs = [
    { q: "How is iSupplyAI different from ChatGPT or Jasper?", a: "ChatGPT gives you one perspective. Jasper gives you templates. iSupplyAI gives you 12 specialized AI agents that actually debate each other — Athena scans competitors, Hermes drafts content, Apollo distributes, Eris challenges every assumption. You walk away with a battle-tested plan, not a single-AI guess. Read the full comparison: see how we stack up against Jasper and Copy.ai." },
    { q: "Do I need a credit card to start?", a: "No. The free Starter plan gives you 50 credits per month, the Strategy Score tool, Website Roast, and Beat My Competitor — all with zero payment information required. Upgrade only when you're ready." },
    { q: "How many credits do I get for free?", a: "50 credits every month on the free plan. Pro ($79/mo) gives you 500 credits plus War Room debates and Artemis lead hunting. Enterprise ($249/mo) gives you 2,000 credits, API access, and team seats." },
    { q: "What are the 12 AI agents and what do they do?", a: "Athena (competitive intel), Hermes (content), Apollo (distribution), Artemis (lead hunting), Ares (aggressive growth), Dionysus (Reddit/community virality), Eris (devil's advocate), Themis (data analytics), Calliope (brand storytelling), Hestia (community building), Plutus (monetization), and Nike (growth hacking). Each agent has a distinct personality, framework, and battle-tested expertise area." },
    { q: "Can I use iSupplyAI for my agency's clients?", a: "Yes. The Enterprise plan supports multi-client workspaces, team seats, white-labeled report exports, and API access so you can plug iSupplyAI into your existing agency stack. Many agencies use Strategy Score and Beat My Competitor as paid client deliverables." },
    { q: "How does the AI content generation work?", a: "Our AI uses GPT-4o and cutting-edge models trained on billions of content pieces. Provide a topic and get professional, original content in seconds." },
    { q: "Is the content plagiarism-free?", a: "Yes! All content is 100% original. Our AI creates unique content based on your specifications, not copying existing material." },
    { q: "What if I'm not satisfied?", a: "We offer a 30-day money-back guarantee. If you're not completely satisfied, we'll refund your entire purchase." },
  ];

  return (
    <section id="faq" className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-white mb-12">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <GlassCard key={i} className="p-6">
              <h3 className="font-bold text-white mb-2">{faq.q}</h3>
              <p className="text-gray-400">{faq.a}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// ===== SEO Fix 4: Content expansion + Fix 5: Internal linking =====

// Who Is iSupplyAI For — 4 personas (~100 words)
const WhoIsForSection = () => {
  const personas = [
    { icon: '🚀', title: 'Startups & Solopreneurs', desc: 'You can\'t afford a 6-figure marketing team. Hire 12 AI strategists instead — for less than one freelance retainer.' },
    { icon: '🏢', title: 'Small Businesses', desc: 'Competing against bigger players with deeper pockets? Our AI gods level the playing field with enterprise-grade competitive intelligence.' },
    { icon: '🎯', title: 'Marketing Agencies', desc: 'Manage 5, 10, or 50 client strategies in one workspace. White-labeled reports, team seats, and API access included on Enterprise.' },
    { icon: '🛒', title: 'E-commerce Brands', desc: 'Win the content + competitive positioning game. Athena finds where competitors are weak; Hermes ships the copy that converts.' },
  ];

  return (
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Who Is iSupplyAI For?</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">If you ship marketing without a 12-person team behind you, this was built for you.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="who-is-for-grid">
          {personas.map((p, i) => (
            <GlassCard key={i} className="p-6 hover:scale-[1.02] transition-transform" data-testid={`persona-${i}`}>
              <div className="text-4xl mb-4">{p.icon}</div>
              <h3 className="text-lg font-bold text-white mb-3">{p.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{p.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats / Social Proof Section (~50 words)
const StatsBarSection = () => {
  const stats = [
    { value: '10,000+', label: 'Strategies generated' },
    { value: '50,000+', label: 'Competitive analyses run' },
    { value: '8 hrs', label: 'Saved per week (avg)' },
    { value: '12', label: 'Specialized AI agents' },
  ];
  return (
    <section className="py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <GlassCard className="p-10 border-purple-500/20" data-testid="stats-bar">
          <p className="text-center text-xs uppercase tracking-widest text-purple-400 mb-8">Trusted by founders, agencies, and growth teams</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`}>
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{s.value}</div>
                <div className="text-xs text-gray-400 mt-2 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </section>
  );
};

// Popular Resources — SEO Fix 5: 15+ internal links to /blog, /glossary, /compare
const PopularResourcesSection = () => {
  const resources = {
    'Free Tools': [
      { label: 'Get your free Strategy Score', to: '/strategy-score', testId: 'res-strategy-score' },
      { label: 'Roast my website (free AI critique)', to: '/roast', testId: 'res-roast' },
      { label: 'Explore trending AI marketing topics', to: '/topics', testId: 'res-topics' },
    ],
    'Compare iSupplyAI': [
      { label: 'iSupplyAI vs Jasper', to: '/compare/jasper', testId: 'res-vs-jasper' },
      { label: 'iSupplyAI vs Copy.ai', to: '/compare/copy-ai', testId: 'res-vs-copyai' },
      { label: 'iSupplyAI vs ChatGPT', to: '/compare/chatgpt', testId: 'res-vs-chatgpt' },
      { label: 'iSupplyAI vs HubSpot AI', to: '/compare/hubspot-ai', testId: 'res-vs-hubspot' },
      { label: 'See all comparisons →', to: '/compare', testId: 'res-compare-all' },
    ],
    'Read The Blog': [
      { label: 'Free AI marketing strategy template', to: '/blog/ai-marketing-strategy-template-free', testId: 'res-blog-template' },
      { label: 'Best AI marketing tools for startups', to: '/blog/best-ai-marketing-tools-for-startups', testId: 'res-blog-tools' },
      { label: 'How to do competitive analysis with AI', to: '/blog/how-to-do-competitive-analysis-with-ai', testId: 'res-blog-competitive' },
      { label: 'Browse the full AI marketing blog →', to: '/blog', testId: 'res-blog-all' },
    ],
    'Learn The Vocabulary': [
      { label: 'What is AI marketing strategy?', to: '/glossary/ai-marketing-strategy', testId: 'res-gloss-strategy' },
      { label: 'What is multi-agent AI?', to: '/glossary/multi-agent-ai', testId: 'res-gloss-multiagent' },
      { label: 'Competitive intelligence definition', to: '/glossary/competitive-intelligence', testId: 'res-gloss-ci' },
      { label: 'Full AI marketing glossary →', to: '/glossary', testId: 'res-gloss-all' },
    ],
  };

  return (
    <section className="py-20 px-6" id="resources">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-purple-400 mb-3">Start exploring</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Popular Resources</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">Free tools, head-to-head comparisons, strategy guides, and definitions — all in one place.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="popular-resources-grid">
          {Object.entries(resources).map(([category, links]) => (
            <GlassCard key={category} className="p-6" data-testid={`resource-cat-${category.toLowerCase().replace(/\s+/g, '-')}`}>
              <h3 className="text-sm font-bold uppercase tracking-wider text-purple-400 mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link, i) => (
                  <li key={i}>
                    <Link to={link.to} className="text-sm text-gray-300 hover:text-purple-300 transition-colors flex items-start gap-2 group" data-testid={link.testId}>
                      <span className="text-purple-500 group-hover:translate-x-0.5 transition-transform mt-0.5">→</span>
                      <span>{link.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA
const FinalCTA = ({ onGetStarted, founderSpots }) => (
  <section className="py-20 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-3xl blur-xl opacity-30" />
        <GlassCard className="relative p-12 border-purple-500/30">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to 10x Your Content?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Only {founderSpots ?? '...'} founder spots left at <span className="text-amber-400 font-bold">$50/month</span> — locks in forever (regular price: $79)
          </p>
          <button onClick={onGetStarted} className="px-10 py-5 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl font-bold text-lg text-white hover:shadow-2xl hover:shadow-purple-500/30 transition-all hover:scale-105">
            Claim Founder Pricing Now
          </button>
          <div className="flex justify-center items-center gap-6 mt-8 text-sm text-gray-400">
            <span>⭐ 4.9/5 rating</span>
            <span>💰 30-day money back</span>
            <span>🔒 Enterprise security</span>
          </div>
        </GlassCard>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="relative py-16 px-6 border-t border-gray-800/50 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/10 via-transparent to-transparent pointer-events-none" />
    
    <div className="relative max-w-7xl mx-auto">
      <div className="grid md:grid-cols-5 gap-8 mb-12">
        {/* Brand column */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI logo — AI marketing strategy platform" title="iSupplyAI — AI Marketing Strategy Platform" className="h-10 w-10 object-contain" />
            <span className="text-2xl font-bold"><GradientText>iSupplyAI</GradientText></span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-sm">
            AI marketing strategy platform. 12 AI agents debate your strategy in real-time — competitive analysis, content creation, lead generation, all personalized to your business data.
          </p>
          <p className="text-gray-500 text-xs mb-4">ISUPPLYAI, LLC</p>
        </div>
        
        {/* Link columns with real links */}
        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Product</h4>
          <ul className="space-y-3">
            {[
              { label: 'Strategy Score', href: '/strategy-score' },
              { label: 'Living War Room', href: '/' },
              { label: 'Website Roast', href: '/' },
              { label: 'Pricing', href: '/' },
            ].map(({ label, href }, j) => (
              <li key={j}>
                <a href={href} className="text-gray-400 hover:text-purple-400 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-px bg-purple-400 transition-all duration-300" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Resources</h4>
          <ul className="space-y-3">
            {[
              { label: 'Blog', href: '/blog' },
              { label: 'Free Downloads', href: '/free-downloads' },
              { label: 'AI Marketing Guide', href: '/blog' },
            ].map(({ label, href }, j) => (
              <li key={j}>
                <a href={href} className="text-gray-400 hover:text-purple-400 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-px bg-purple-400 transition-all duration-300" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Company</h4>
          <ul className="space-y-3">
            {[
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Refund Policy', href: '/refund-policy' },
              { label: 'Contact Us', href: 'mailto:support@isupplyai.com' },
            ].map(({ label, href }, j) => (
              <li key={j}>
                <a href={href} className="text-gray-400 hover:text-purple-400 transition-colors text-sm flex items-center gap-2 group">
                  <span className="w-0 group-hover:w-2 h-px bg-purple-400 transition-all duration-300" />
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Bottom bar */}
      <div className="pt-8 border-t border-gray-800/50 flex flex-col md:flex-row justify-between items-center gap-6">
        <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} ISUPPLYAI, LLC. All rights reserved.</p>
        
        <div className="flex items-center gap-6 text-xs text-gray-500">
          <span>SSL Secured</span>
          <span>SOC 2 Compliant Infrastructure</span>
          <span>GDPR Ready</span>
        </div>
      </div>
    </div>
  </footer>
);

// ============================================================
// MAIN COMPONENT
// ============================================================

const PremiumLandingPage = ({ onGetStarted, onLogin, onDemoLogin, demoLoading }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [founderSpots, setFounderSpots] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/pricing/founder-status`)
      .then(r => r.json())
      .then(d => { if (d.spots_remaining !== undefined) setFounderSpots(d.spots_remaining); })
      .catch(() => {});
  }, []);

  // SEO: SiteNavigationElement + ItemList schemas for primary nav + popular resources
  useEffect(() => {
    const siteNav = {
      '@context': 'https://schema.org',
      '@type': 'SiteNavigationElement',
      name: ['Strategy Score', 'Website Roast', 'Compare', 'Blog', 'Glossary', 'Topics'],
      url: [
        'https://isupplyai.com/strategy-score',
        'https://isupplyai.com/roast',
        'https://isupplyai.com/compare',
        'https://isupplyai.com/blog',
        'https://isupplyai.com/glossary',
        'https://isupplyai.com/topics',
      ],
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://isupplyai.com/' },
      ],
    };
    const scripts = [siteNav, breadcrumb].map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `landing-schema-${i}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => scripts.forEach((el) => el.remove());
  }, []);

  return (
    <div className="min-h-screen bg-[#030305] text-white">
      <>
        <title>iSupplyAI | AI Marketing Strategy Platform</title>
        <meta name="description" content="12 AI agents debate your marketing strategy in real time. Competitive analysis, content creation, lead generation — personalized to your business data. Free to start." />
        <link rel="canonical" href="https://isupplyai.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content="iSupplyAI | AI Marketing Strategy Platform" />
        <meta property="og:description" content="12 AI agents debate your marketing strategy in real time. Competitive analysis, content creation, lead generation — personalized to your business data." />
        <meta property="og:url" content="https://isupplyai.com" />
        <meta property="og:image" content="https://static.prod-images.emergentagent.com/jobs/84aa8e5d-0e87-49f6-ba56-f09881296b20/images/c356f67bf8d2a3802154509c71e31a12486d2927268372762a5818c4ede50351.png" />
        <meta property="og:image:width" content="1536" />
        <meta property="og:image:height" content="1024" />
        <meta property="og:image:alt" content="iSupplyAI War Room — AI marketing strategists debating your business strategy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content="iSupplyAI | AI Marketing Strategy Platform" />
        <meta name="twitter:description" content="12 AI agents debate your marketing strategy in real time. Competitive analysis, content creation, lead generation — personalized to your business data." />
        <meta name="twitter:image" content="https://static.prod-images.emergentagent.com/jobs/84aa8e5d-0e87-49f6-ba56-f09881296b20/images/c356f67bf8d2a3802154509c71e31a12486d2927268372762a5818c4ede50351.png" />
        <meta name="twitter:image:alt" content="iSupplyAI — AI Marketing Strategy Platform" />
      </>
      <FloatingOrbs />
      <ParticleField />
      <GridPattern />
      
      <div className="relative z-10">
        <Navigation onGetStarted={onGetStarted} onLogin={onLogin} isScrolled={isScrolled} />
        <HeroSection onGetStarted={onGetStarted} onDemoLogin={onDemoLogin} demoLoading={demoLoading} onShareFeedback={() => setShowFeedback(true)} />
        <SocialProofTicker />
        <FeaturedInSection />
        <TrustBadgesSection />
        <StrategyScoreSection />
        <FeaturedDebateSection onGetStarted={onGetStarted} />
        <PricingSection onGetStarted={onGetStarted} founderSpots={founderSpots} />
        <HowItWorksSection />
        <FeaturesSection />
        <PricingFAQ />
        <ChatGPTComparison />
        <WhoIsForSection />
        <StatsBarSection />
        <TestimonialsSection />
        <GeneralFAQ />
        <PopularResourcesSection />
        <FinalCTA onGetStarted={onGetStarted} founderSpots={founderSpots} />
        <Footer />
      </div>

      <style>{`
        @keyframes gradient { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes float { 
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; } 
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.5; }
          50% { transform: translateY(-40px) translateX(-5px); opacity: 0.3; } 
          75% { transform: translateY(-20px) translateX(15px); opacity: 0.4; }
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes gradient-border {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient { background-size: 200% auto; animation: gradient 3s linear infinite; }
        .animate-scroll { animation: scroll 30s linear infinite; }
        .animate-float { animation: float 20s ease-in-out infinite; }
        .animate-fadeInUp { animation: fadeInUp 0.8s ease-out forwards; opacity: 0; }
        .animate-shimmer { animation: shimmer 3s ease-in-out infinite; }
        .animate-gradient-border { background-size: 200% 200%; animation: gradient-border 3s ease infinite; }
      `}</style>
      <FeedbackModal isOpen={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
};

export default PremiumLandingPage;
